import React, { useEffect } from 'react';
import styles from './AdminProjectDiagnosticsDetail.module.scss';
import AdminProjectDetailInfo from '../AdminProjectDetailInfo/AdminProjectDetailInfo';
import AdminProjectDetailQuestionnaires from '../AdminProjectDetailQuestionnaires/AdminProjectDetailQuestionnaires';
import AdminProjectDetailProgress from '../AdminProjectDetailProgress/AdminProjectDetailProgress';
import AdminProjectDetailParticipants from '../AdminProjectDetailParticipants/AdminProjectDetailParticipants';
import AdminProjectDetailAdmin from '../AdminProjectDetailAdmin/AdminProjectDetailAdmin';
import AdminProjectDetailStage from '../AdminProjectDetailStage/AdminProjectDetailStage';
import AdminProjectDetailActions from '../AdminProjectDetailActions/AdminProjectDetailActions';
import FileEarmarkPlusFillIcon from 'react-bootstrap-icons/dist/icons/file-earmark-plus-fill';
import IAdminProject from '../../../types/IAdminProject';
import { generatePath, useNavigate } from 'react-router-dom';
import {
    ADMIN_INVITE_PATH,
    ADMIN_PROJECT_EDIT_PATH,
    ADMIN_PROJECT_JOBMATCH_REPORTS_PATH,
    ADMIN_PROJECT_PACKAGES_PATH,
    ADMIN_PROJECT_PARTICIPANTS_PATH,
    ADMIN_PROJECT_QUESTIONNAIRES_PATH,
    ADMIN_PROJECT_REPORTS_PATH,
    ADMIN_PROJECT_TRANSLATIONS_PATH,
} from '../../../../constants/route.constants';
import AdminProjectLayout from '../AdminProjectLayout/AdminProjectLayout';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../../../reducers/store';
import AdminProjectSlice from '../../../slices/AdminProjectSlice';
import useAdminProjectParams from '../../../hooks/useAdminProjectParams';
import { useTranslate } from 'react-translate.ts';
import AdminProjectDetailReports from '../AdminProjectDetailReports/AdminProjectDetailReports';
import AdminProjectDetailInvites from '../AdminProjectDetailInvites/AdminProjectDetailInvites';
import { cx } from '@emotion/css';
import AdminProjectActions from '../../project/AdminProjectActions/AdminProjectActions';
import useProjectDeleteDialog from '../../../hooks/useProjectDeleteDialog';

type AdminProjectDiagnosticsDetailProps = {
    project: IAdminProject;
    loading: boolean;
    uploading: boolean;
};

const LINKS = [
    {
        title: 'Upravit',
        link: generatePath(ADMIN_PROJECT_EDIT_PATH, {
            '*': '',
        }),
    },
    {
        title: 'Preklady',
        link: generatePath(ADMIN_PROJECT_EDIT_PATH, {
            '*': ADMIN_PROJECT_TRANSLATIONS_PATH,
        }),
    },
    {
        title: 'Balicky',
        link: generatePath(ADMIN_PROJECT_EDIT_PATH, {
            '*': ADMIN_PROJECT_PACKAGES_PATH,
        }),
    },
    {
        title: 'Dotazniky',
        link: generatePath(ADMIN_PROJECT_EDIT_PATH, {
            '*': ADMIN_PROJECT_QUESTIONNAIRES_PATH,
        }),
    },
    {
        title: 'Ucastnici',
        icon: <FileEarmarkPlusFillIcon />,
        link: generatePath(ADMIN_PROJECT_EDIT_PATH, {
            '*': ADMIN_PROJECT_PARTICIPANTS_PATH,
        }),
    },
    {
        title: 'Pozvanky',
        link: generatePath(ADMIN_INVITE_PATH, {
            '*': '',
        }),
    },
    {
        title: 'Vysledky',
        link: generatePath(ADMIN_PROJECT_REPORTS_PATH),
    },
    {
        title: 'Jobmatch Vysledky',
        link: generatePath(
            `${ADMIN_PROJECT_REPORTS_PATH}/${ADMIN_PROJECT_JOBMATCH_REPORTS_PATH}`,
        ),
    },
];

export default function AdminProjectDiagnosticsDetail(
    props: AdminProjectDiagnosticsDetailProps,
) {
    const { project, loading, uploading } = props;
    const dispatch = useDispatch<AppDispatch>();
    const { projectId, productId } = useAdminProjectParams();
    const { data: participants, loading: participantsLoading } = useSelector(
        AdminProjectSlice.state.getProjectParticipants,
    );
    const { data: admins, loading: adminsLoading } = useSelector(
        AdminProjectSlice.state.getAdmins,
    );
    const { data: reports, loading: reportsLoading } = useSelector(
        AdminProjectSlice.state.getReports,
    );
    const { data: jobMatchReports, loading: jobMatchReportsLoading } =
        useSelector(AdminProjectSlice.state.getJobMatchReports);
    const { data: invites, loading: invitesLoading } = useSelector(
        AdminProjectSlice.state.getInvites,
    );
    const navigate = useNavigate();

    const translate = useTranslate();
    useEffect(() => {
        if (!projectId || productId === null || productId === undefined) {
            return;
        }

        dispatch(
            AdminProjectSlice.actions.getProjectParticipants({
                productId,
                projectId,
                limit: 5,
            }),
        );
        dispatch(
            AdminProjectSlice.actions.getAdmins({
                productType: productId,
                projectId,
                limit: 5,
            }),
        );
        dispatch(
            AdminProjectSlice.actions.getInvites({
                productId,
                projectId,
                limit: 5,
            }),
        );
        dispatch(
            AdminProjectSlice.actions.getReports({
                productType: productId,
                projectId,
                limit: 5,
            }),
        );
        dispatch(
            AdminProjectSlice.actions.getJobMatchReports({
                productType: productId,
                projectId,
                limit: 5,
            }),
        );
    }, [projectId, productId]);

    const {
        openDeleteDialog,
        openArchiveDialog,
        openAnonymizeDialog,
        openCopyDialog,
        dialogs,
    } = useProjectDeleteDialog(productId, {
        postDelete: navigateToProjects,
        postCopy: navigateToNewProject,
    });

    return (
        <AdminProjectLayout heading={project?.name} loading={loading}>
            {dialogs}
            <div className={styles.top}>
                <AdminProjectDetailStage
                    currentStage={project?.phase}
                    disabled={loading || uploading}
                    steps={[
                        { title: translate('Pripravna faze') },
                        { title: translate('Exekucni faze') },
                        { title: translate('Vyhoidnoce') },
                    ]}
                    onChange={changePhase(productId, projectId, project)}
                />
                <AdminProjectActions
                    project={project}
                    openDeleteDialog={openDeleteDialog}
                    openArchiveDialog={openArchiveDialog}
                    openAnonymizeDialog={openAnonymizeDialog}
                    openCopyDialog={openCopyDialog}
                />
            </div>
            <AdminProjectDetailActions actions={LINKS} />
            <div className={cx(styles.grid, styles.columns, styles.content)}>
                <div className={styles.grid}>
                    <AdminProjectDetailInfo
                        project={project}
                        loading={loading}
                    />
                    <AdminProjectDetailParticipants
                        participants={participants}
                        loading={participantsLoading}
                        withLink
                    />
                    <AdminProjectDetailAdmin
                        admins={admins}
                        loading={!!adminsLoading}
                    />
                    <AdminProjectDetailReports
                        title={translate('Výsledky')}
                        reports={reports}
                        loading={!!reportsLoading}
                        link={ADMIN_PROJECT_REPORTS_PATH}
                    />
                    <AdminProjectDetailReports
                        title={translate('Job Match Výsledky')}
                        reports={jobMatchReports}
                        loading={!!jobMatchReportsLoading}
                        link={`${ADMIN_PROJECT_REPORTS_PATH}/${ADMIN_PROJECT_JOBMATCH_REPORTS_PATH}`}
                    />
                </div>
                <div className={styles.grid}>
                    <AdminProjectDetailProgress
                        stats={project.participantStatistics}
                    />

                    <AdminProjectDetailInvites
                        invites={invites}
                        loading={!!invitesLoading}
                    />
                    <AdminProjectDetailQuestionnaires
                        questionnaires={project.selectedQuestionnaires}
                        loading={loading}
                    />
                </div>
            </div>
        </AdminProjectLayout>
    );

    function changePhase(
        productId: number,
        projectId: number,
        project: IAdminProject,
    ) {
        return (phase: number) => {
            dispatch(
                AdminProjectSlice.actions.saveProject({
                    projectId,
                    productId,
                    phase,
                    primaryLanguage: project.primaryLanguage || 'cs',
                }),
            );
        };
    }

    function navigateToProjects() {
        navigate('../');
    }

    function navigateToNewProject(projectId: number) {
        navigate(`../${projectId}`);
    }
}
